import { DatepickerAlt } from '@chiroup/components';
import { classNames } from '@chiroup/core/functions/classNames';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement } from 'react';
import Alert from './common/Alert';

type Props = {
  selectedDate?: Dayjs;
  setSelectedDate: (date: Dayjs) => void;
  onChange: (value: {
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
    duration: number;
  }) => void;
  primaryColor: string;
  onSelectDate: (date: string) => void;
  selectedDay?: string;
  onChangeMonth: (year: string, month: string) => void;
  dottedDays: { [key: number]: boolean };
  loading: boolean;
  availableTimes: {
    providerId: string;
    providerName?: string;
    profileImage?: string | null;
    times: {
      date: string;
      startTime: Dayjs;
      endTime: Dayjs;
      provider: string;
      duration: number;
    }[];
  }[];
  viewDate: dayjs.Dayjs;
  setViewDate: (date: dayjs.Dayjs) => void;
  title?: ReactElement;
  className?: string;
  compact?: boolean;
  error?: string | null;
};

const ScheduleTime: React.FC<Props> = ({
  selectedDate,
  setSelectedDate,
  onChange,
  primaryColor,
  onSelectDate,
  selectedDay,
  onChangeMonth,
  dottedDays,
  loading,
  availableTimes,
  viewDate,
  setViewDate,
  title = (
    <div className="flex items-center gap-1">
      <span className="text-sm text-gray-900 whitespace-nowrap">
        Select a time
      </span>
      <div className="w-full border-t border-gray-300 flex-grow ml-2" />
    </div>
  ),
  className,
  compact = false,
  error,
}) => {
  return (
    <div className={className}>
      <div className="flex flex-col gap-6" id="time">
        {title}
        {!!error && <Alert title="Error" error={error} />}
        <div className={classNames('w-full sm:w-[460px] mx-auto')}>
          <DatepickerAlt
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onChange={onSelectDate}
            onChangeMonth={onChangeMonth}
            primaryColor={primaryColor}
            dottedDays={dottedDays}
            loading={loading}
            viewDate={viewDate}
            setViewDate={setViewDate}
          />
        </div>
        {!!selectedDay && (
          <div className="mt-6">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-900 whitespace-nowrap">
                Availability for {selectedDay}
              </span>
              <div className="w-full border-t border-gray-300 flex-grow ml-2" />
            </div>
            <div className="mt-4 grid grid-cols-1 gap-y-2 sm:gap-x-4">
              {availableTimes?.map((provider, i) => {
                return (
                  <div
                    key={i}
                    className={classNames(
                      'border-gray-300',
                      'shadow-sm relative flex rounded-lg border bg-white p-4 focus:outline-none',
                    )}
                  >
                    <span className="flex flex-1">
                      <div className="flex gap-x-4">
                        {compact ? null : provider.profileImage ? (
                          <img
                            className="h-12 w-12 flex-none rounded-full bg-gray-50"
                            src={provider.profileImage}
                            alt={provider.providerName}
                          />
                        ) : (
                          <span className="inline-block h-12 w-12 overflow-hidden rounded-full bg-gray-100 mx-auto flex-none">
                            <svg
                              className="h-full w-full text-gray-300"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </span>
                        )}
                        <div className="min-w-0 flex-auto text-left">
                          <span className="text-sm font-semibold leading-6 text-gray-900">
                            {provider.providerName}
                          </span>
                          <span className="mt-4 text-xs leading-5 text-gray-500 flex flex-row gap-2 flex-wrap">
                            {provider.times.map((time, i) => (
                              <span
                                key={i}
                                className="cursor-pointer inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 w-32 justify-between hover:bg-gray-100 hover:text-gray-900 hover:ring-gray-500"
                                onClick={() => onChange(time)}
                              >
                                <span>{time.startTime.format('h:mma')}</span>
                                <span>-</span>
                                <span>{time.endTime.format('h:mma')}</span>
                              </span>
                            ))}
                          </span>
                        </div>
                      </div>
                    </span>
                    <span
                      className={classNames(
                        'border-2',
                        'border-transparent',
                        'pointer-events-none absolute -inset-px rounded-lg',
                      )}
                      aria-hidden="true"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleTime;
